// tempoReale.ts — collegamento WebSocket con il server e sincronizzazione dell'orologio.
// Si ricollega da solo con attesa crescente (1 s, 2 s, 4 s… fino a 30 s) e quando torna la rete.
// La stanza di gioco (stanza.ts) lo usa per mandare e ricevere i messaggi della partita: a ogni nuovo
// collegamento la stanza rientra da sola (alCollegamento).
import { useSyncExternalStore } from "react";
import {
  aggiungiCampione,
  stimaOrologio,
  type CampioneOrologio,
  type StimaOrologio,
} from "../../../condiviso/orologio.ts";
import type { MessaggioClient, MessaggioServer } from "../../../condiviso/protocollo.ts";
import {
  BASE_API,
  CAMPIONI_OROLOGIO,
  IMP_APP,
  RICONNESSIONE_MASSIMA_PREDEFINITA_SECONDI,
  RICONNESSIONE_MIN_MS,
  SINCRONIZZA_PREDEFINITA_SECONDI,
} from "../costanti.ts";
import { eNumero, leggiImpostazione } from "./impostazioni.ts";

export interface StatoTempoReale {
  stato: "collegamento" | "collegato" | "scollegato";
  stima: StimaOrologio | null;
  versioneServer: string | null;
}

const PING_RAPIDI = 3; // appena collegati: tre ping ravvicinati per una stima subito buona
const INTERVALLO_PING_RAPIDI_MS = 400;

class TempoReale {
  private socket: WebSocket | null = null;
  private campioni: CampioneOrologio[] = [];
  private attesa = RICONNESSIONE_MIN_MS;
  private timerPing: ReturnType<typeof setInterval> | null = null;
  private timerRiconnessione: ReturnType<typeof setTimeout> | null = null;
  private prossimoId = 1;
  private ascoltatori = new Set<() => void>();
  private ascoltatoriMessaggi = new Set<(m: MessaggioServer) => void>();
  private ascoltatoriCollegamento = new Set<() => void>();
  private avviato = false;
  stato: StatoTempoReale = { stato: "collegamento", stima: null, versioneServer: null };

  avvia(): void {
    if (this.avviato) return;
    this.avviato = true;
    window.addEventListener("online", () => this.collegaSubito());
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.collegaSubito();
    });
    this.collega();
  }

  iscrivi = (a: () => void): (() => void) => {
    this.ascoltatori.add(a);
    return () => this.ascoltatori.delete(a);
  };

  /** Messaggi del server (tutti tranne il pong dell'orologio). */
  ascolta(f: (m: MessaggioServer) => void): () => void {
    this.ascoltatoriMessaggi.add(f);
    return () => this.ascoltatoriMessaggi.delete(f);
  }

  /** Chiamata a ogni (ri)collegamento: la stanza rientra. Se si è già collegati, subito. */
  alCollegamento(f: () => void): () => void {
    this.ascoltatoriCollegamento.add(f);
    if (this.socket?.readyState === WebSocket.OPEN) f();
    return () => this.ascoltatoriCollegamento.delete(f);
  }

  /** Manda un messaggio; false se in questo momento non si è collegati (la stanza lo rimanderà). */
  invia(m: MessaggioClient): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(m));
    return true;
  }

  /** Ora del server stimata (ms), per il conto alla rovescia e per datare le risate. */
  oraServer(): number {
    return performance.timeOrigin + performance.now() + (this.stato.stima?.scartoMs ?? 0);
  }

  private aggiorna(nuovo: Partial<StatoTempoReale>): void {
    this.stato = { ...this.stato, ...nuovo };
    this.ascoltatori.forEach((a) => a());
  }

  private indirizzo(): string {
    if (BASE_API) return `${BASE_API.replace(/^http/, "ws")}/ws`;
    const protocollo = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocollo}//${window.location.host}/ws`;
  }

  private collegaSubito(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) return;
    if (this.timerRiconnessione) clearTimeout(this.timerRiconnessione);
    this.attesa = RICONNESSIONE_MIN_MS;
    this.collega();
  }

  private collega(): void {
    this.aggiorna({ stato: "collegamento" });
    let s: WebSocket;
    try {
      s = new WebSocket(this.indirizzo());
    } catch {
      this.programmaRiconnessione();
      return;
    }
    this.socket = s;
    s.onopen = () => {
      this.attesa = RICONNESSIONE_MIN_MS;
      this.aggiorna({ stato: "collegato" });
      for (let i = 0; i < PING_RAPIDI; i++) setTimeout(() => this.ping(), i * INTERVALLO_PING_RAPIDI_MS);
      const secondi = leggiImpostazione(IMP_APP.sincronizzaSecondi, SINCRONIZZA_PREDEFINITA_SECONDI, eNumero);
      this.timerPing = setInterval(() => this.ping(), secondi * 1000);
      this.ascoltatoriCollegamento.forEach((f) => f());
    };
    s.onmessage = (ev) => this.ricevi(ev.data);
    s.onclose = () => {
      if (this.timerPing) clearInterval(this.timerPing);
      this.timerPing = null;
      if (this.socket === s) this.socket = null;
      this.aggiorna({ stato: "scollegato" });
      this.programmaRiconnessione();
    };
    s.onerror = () => s.close();
  }

  private programmaRiconnessione(): void {
    if (this.timerRiconnessione) clearTimeout(this.timerRiconnessione);
    this.timerRiconnessione = setTimeout(() => this.collega(), this.attesa);
    const massimaMs =
      leggiImpostazione(IMP_APP.riconnessioneMassimaSecondi, RICONNESSIONE_MASSIMA_PREDEFINITA_SECONDI, eNumero) * 1000;
    this.attesa = Math.min(this.attesa * 2, massimaMs);
  }

  private ping(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    const m: MessaggioClient = { tipo: "ping", id: this.prossimoId++, t0: performance.timeOrigin + performance.now() };
    this.socket.send(JSON.stringify(m));
  }

  private ricevi(dati: unknown): void {
    if (typeof dati !== "string") return;
    let m: MessaggioServer;
    try {
      m = JSON.parse(dati) as MessaggioServer;
    } catch {
      return;
    }
    if (m.tipo === "benvenuto") {
      this.aggiorna({ versioneServer: m.versione });
    } else if (m.tipo === "pong") {
      const t2 = performance.timeOrigin + performance.now();
      this.campioni = aggiungiCampione(this.campioni, { t0: m.t0, t1: m.tServer, t2 }, CAMPIONI_OROLOGIO);
      this.aggiorna({ stima: stimaOrologio(this.campioni) });
      return;
    }
    this.ascoltatoriMessaggi.forEach((f) => f(m));
  }
}

export const tempoReale = new TempoReale();

export function useTempoReale(): StatoTempoReale {
  return useSyncExternalStore(tempoReale.iscrivi, () => tempoReale.stato);
}
